import { CheckoutContextProvider } from "@@/carts/checkout.context";
import { CartContextProvider } from "@@/carts/multi-carts/cart.context";
import { PageLoad } from "@@/pages/page-load";
import { _Buyable_ProductPage } from "@@/products/buyable.product-page";
import { _Bookable_ProductPage } from "@@/routes/products/$productId/_bookable/bookable.product-page";
import { ProductPageV2 } from "@@/routes/products/$productId/_product_v2.page";
import { BrandingProvider } from "@@/routes/shared/branding-context";
import { useProductLoader } from "@@/routes/shared/loaders/product-loader";
import { useProviderLoader } from "@@/routes/shared/loaders/provider-loader";
import { _Voucher_ProductPage } from "@@/routes/vouchers/$voucher/voucher.product-page";
import { LanguageProviderForProvider } from "@@/settings/language-context";
import { useKeyboardNavShortcut } from "@@/shared/use-keyboard-nav-shortcut";
import { brandingV2ModeAtom } from "@@/users/modal/dev-branding-v2";
import type { Product, ProductId, Provider } from "@towni/common";
import { productIdZodSchema, translation } from "@towni/common";
import { useAtom } from "jotai";
import { useParams } from "react-router";
import { _Accommodation_ProductPage } from "./_accommodation/accommodation.product-page";
import { _GiftCard_ProductPage } from "./_gift_card/gift-card.product-page";

type Props = {
    productId?: ProductId;
};

const ProductPage = (props: Props) => {
    const paramProductId = useParams<{ productId?: string }>().productId;
    const productId = productIdZodSchema.safeParse(
        props.productId ?? paramProductId,
    );
    const [product, productIsPending] = useProductLoader(
        productId.success ? productId.data : undefined,
    );
    const [provider, providerIsPending] = useProviderLoader(
        product?.providerId,
    );

    if (productIsPending || providerIsPending)
        return (
            <PageLoad
                text={translation({
                    sv: "Laddar produkt...",
                    en: "Loading product...",
                    de: "Produkt wird geladen...",
                })}
            />
        );

    return (
        <LanguageProviderForProvider provider={provider}>
            <CartContextProvider key={provider._id} providerId={provider._id}>
                <CheckoutContextProvider>
                    <BrandingProvider providerId={provider._id}>
                        <ProductPageSelector
                            product={product}
                            provider={provider}
                        />
                    </BrandingProvider>
                </CheckoutContextProvider>
            </CartContextProvider>
        </LanguageProviderForProvider>
    );
};

const ProductPageSelector = (props: {
    product: Product;
    provider: Provider;
}) => {
    const [brandingMode, _] = useAtom(brandingV2ModeAtom);

    const { product, provider } = props;

    const usesProductPagesV2 = provider.options.usesProductPagesV2;

    useKeyboardNavShortcut({
        keys: ["v", "p"],
        url: `/bo/providers/${provider._id}/products/${product._id}`,
        modifiers: {
            ctrl: true,
        },
    });
    useKeyboardNavShortcut({
        keys: ["v", "o"],
        url: `/bo/providers/${provider._id}/products/${product._id}`,
        hrefTarget: "_blank",
        modifiers: {
            ctrl: true,
        },
    });

    if (usesProductPagesV2) {
        return <ProductPageV2 product={product} provider={provider} />;
    }

    if (brandingMode) {
        return <ProductPageV2 product={product} provider={provider} />;
    }

    switch (product.acquirableAs) {
        case "BOOKABLE":
            return (
                <_Bookable_ProductPage product={product} provider={provider} />
            );
        case "BUYABLE":
            return (
                <_Buyable_ProductPage
                    provider={provider}
                    product={product}
                    hideLinkToShop={false}
                />
            );
        case "VOUCHER":
            return (
                <_Voucher_ProductPage
                    voucherId={product._id}
                    provider={provider}
                />
            );
        case "GIFTCARD":
            return (
                <_GiftCard_ProductPage
                    giftCardId={product._id}
                    provider={provider}
                />
            );
        case "ACCOMMODATION":
            return (
                <_Accommodation_ProductPage
                    product={product}
                    provider={provider}
                />
            );
    }
};

export { ProductPage };
